/**
 * Card and paper geometry shared by the place card designer and the PDF generator.
 */

import type { CardSize, PaperSize, PlaceCardTemplate } from '@/types';

export const PAPER_SIZE_INCHES: Record<PaperSize, { width: number; height: number }> = {
  A4: { width: 8.27, height: 11.69 },
  A5: { width: 5.83, height: 8.27 },
  Letter: { width: 8.5, height: 11 },
  Legal: { width: 8.5, height: 14 },
};

export const MIN_CUSTOM_CARD_INCHES = 1;

/** Cards may overshoot the sheet by this much (the A5 card is 8.3in on 8.27in A5 paper). */
const FIT_TOLERANCE_INCHES = 0.05;

export function placeCardSizeInches(
  cardSize: CardSize,
  customWidth?: number,
  customHeight?: number
): { width: number; height: number } {
  if (cardSize === 'custom') {
    return {
      width: customWidth && customWidth > 0 ? customWidth : 4,
      height: customHeight && customHeight > 0 ? customHeight : 6,
    };
  }

  const [width, height] = cardSize.split('x').map(Number);
  return { width, height };
}

export function templateCardSizeInches(template: Pick<PlaceCardTemplate, 'cardSize' | 'customCardWidth' | 'customCardHeight'>) {
  return placeCardSizeInches(template.cardSize, template.customCardWidth, template.customCardHeight);
}

/** How many cards of the given length fit along a page length (at least 1). */
export function cardsThatFit(pageLength: number, cardLength: number): number {
  return Math.max(1, Math.floor((pageLength + FIT_TOLERANCE_INCHES) / cardLength));
}

export function placeCardGrid(
  paperSize: PaperSize,
  cardWidth: number,
  cardHeight: number
): { cardsPerRow: number; cardsPerCol: number; maxCardsPerPage: number; cardFitsPaper: boolean } {
  const paper = PAPER_SIZE_INCHES[paperSize] ?? PAPER_SIZE_INCHES.A4;
  const cardsPerRow = cardsThatFit(paper.width, cardWidth);
  const cardsPerCol = cardsThatFit(paper.height, cardHeight);
  return {
    cardsPerRow,
    cardsPerCol,
    maxCardsPerPage: cardsPerRow * cardsPerCol,
    cardFitsPaper:
      cardWidth <= paper.width + FIT_TOLERANCE_INCHES && cardHeight <= paper.height + FIT_TOLERANCE_INCHES,
  };
}
